<template>
  <div>
    <v-card flat>
      <v-toolbar class="toolbar no-display" flat dense>
        <v-toolbar-title>
          <strong>Receipt</strong>
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn v-if="receiptData" class="ma-2 no-display" tile outlined color="warning" @click="printReceipt">
          <v-icon left>mdi-printer</v-icon> PRINT
        </v-btn>
      </v-toolbar>

      <v-radio-group v-model="selectedCopy" row mandatory class="ma-4 no-display justify-center" v-if="receiptData">
        <v-radio label="Both" value="both" color="error"></v-radio>
        <v-radio label="Office Copy" value="office" color="error"></v-radio>
        <v-radio label="Student Copy" value="student" color="error"></v-radio>
      </v-radio-group>

      <div v-if="receiptData" id="des-receipt-print-area">
        <div class="receipt-outer green-dotted">
          <!-- OFFICE COPY -->
          <div class="receipt-copy office" :class="{ 'full-width': selectedCopy !== 'both' }"
            v-if="selectedCopy === 'both' || selectedCopy === 'office'">
            <div class="header-block">
              <div class="copy-label">OFFICE COPY</div>
              <!-- Trust name + GST line -->
              <div class="trust-gst-line">
                <!-- <div class="trust-name">{{ org_basic_data.trust_name }}</div> -->
                <div class="gst-number">GST NO : 27AAATD3141P1ZL</div>
              </div>

              <!-- Logo + School name + Address -->
              <div class="school-header-row">
                <div class="school-logo-wrapper">
                  <img v-if="org_basic_data.school_logo || org_basic_data.trust_logo"
                    :src="org_basic_data.school_logo || org_basic_data.trust_logo" alt="School Logo"
                    class="school-logo" />
                </div>

                <div class="school-info-center">
                  <div class="trust-name">{{ org_basic_data.trust_name }}</div>
                  <h2 class="school-name">{{ org_basic_data.school_name }}</h2>
                  <div class="school-address">
                    {{ org_basic_data.address }}
                  </div>
                </div>

                <!-- Right side empty to balance layout -->
                <div class="right-placeholder"></div>
              </div>

              <div class="receipt-title-line">
                FEE RECEIPT
              </div>
            </div>



            <table class="info-table" cellpadding="0" cellspacing="0">
              <tr>
                <td class="label">Rec. No. :</td>
                <td class="value"><b>{{ receiptData.receipt_no || receiptData.rec_no || '-' }}</b></td>
                <td class="label">Adm. No. :</td>
                <td class="value"><b>{{ receiptData.admission_form_no || receiptData.admission_no || '-' }}</b></td>
              </tr>
              <tr>
                <td class="label">Class :</td>
                <td class="value"><b>{{ receiptData.standard || receiptData.class || '-' }}</b></td>
                <td class="label">Date :</td>
                <td class="value"><b>{{ receiptData.receipt_date || '-' }}</b></td>
              </tr>
              <tr>
                <td class="label">Admitted for :</td>
                <td class="value"><b>{{ receiptData.admitted_for || receiptData.ay || '-' }}</b></td>
                <!-- <td class="label empty-cell"></td>
                <td class="value empty-cell"></td> -->

                <td class="label">Student Id :</td>
                <td class="value"><b>{{ receiptData.student_id || receiptData.registration_number || '-' }}</b></td>
              </tr>
              <tr>
                <td class="label">Roll No :</td>
                <td class="value"><b>{{ receiptData.roll_no || '-' }}</b></td>

                <td class="label">Name :</td>
                <td colspan="3" class="value name-full"><b>{{ safeName }}</b></td>
              </tr>
            </table>

            <div class="fee-header">Received the following</div>

            <table class="fee-table" cellpadding="0" cellspacing="0">
              <tbody>
                <tr v-for="item in receiptData.student_receipt_details || []" :key="item.id || item.component">
                  <td class="fee-desc">{{ item.component || item.fee_head || 'Unknown Fee' }}</td>
                  <td class="fee-amount">{{ formatAmount(item.amount) }}</td>
                </tr>
              </tbody>
            </table>

            <div class="total-line">
              <span class="total-label"><b>TOTAL :</b></span>
              <span class="total-value">₹ {{ safeTotalAmountNoSymbol }}.00</span>
            </div>

            <div class="bottom-section">
              <div class="bottom-line">In words : <b>{{ receiptData.amount_in_words || '-' }}</b></div>
              <div class="bottom-line">Amount : {{ formatAmount(receiptData.total_fees_amount, false) }}.00</div>
              <div class="bottom-line">Remarks : {{ receiptData.remark || '-' }}</div>

              <!-- Signature block – right aligned -->
              <div class="signature-block">
                <div class="signature-line-wrapper">
                  <!-- <div class="signature-line"></div> -->
                  <div class="sig-text">RECEIVER'S SIGNATURE</div>
                </div>

                <!-- <div class="receiver-info">
                  N1-RADNYA SUMANT{{ receiptData.print_date ? ' ' + receiptData.print_date : '' }}<br>
                  NEMSADMIN
                </div> -->
              </div>

              <!-- Horizontal line separator -->
              <!-- <hr class="separator-line" /> -->

              <!-- Page info -->
              <div class="footer-info">
                <div class="footer-left">
                  {{ receiptData.print_date ? receiptData.print_date : ''}}
                </div>
                <!-- <div class="footer-center">
                  {{ receiptData.print_username || 'NEMSADMIN' }}
                </div> -->
                <div class="footer-right">
                  Page 1 of 1
                </div>
              </div>
            </div>
          </div>

          <div class="partition-line" v-if="selectedCopy === 'both'"></div>

          <!-- STUDENT COPY -->
          <div class="receipt-copy student" :class="{ 'full-width': selectedCopy !== 'both' }"
            v-if="selectedCopy === 'both' || selectedCopy === 'student'">
            <div class="header-block">
              <div class="copy-label">STUDENT COPY</div>
              <div class="trust-gst-line">
                <!-- <div class="trust-name">{{ org_basic_data.trust_name }}</div> -->
                <div class="gst-number student-gst">GST NO : 27AAATD3141P1ZL</div>
              </div>

              <div class="school-header-row">
                <div class="school-logo-wrapper">
                  <img v-if="org_basic_data.school_logo || org_basic_data.trust_logo"
                    :src="org_basic_data.school_logo || org_basic_data.trust_logo" alt="School Logo"
                    class="school-logo" />
                </div>

                <div class="school-info-center">
                  <div class="trust-name">{{ org_basic_data.trust_name }}</div>
                  <h2 class="school-name">{{ org_basic_data.school_name }}</h2>
                  <div class="school-address">
                    {{ org_basic_data.address }}
                  </div>
                </div>

                <div class="right-placeholder"></div>
              </div>

              <div class="receipt-title-line">
                FEE RECEIPT
              </div>
            </div>



            <table class="info-table" cellpadding="0" cellspacing="0">
              <tr>
                <td class="label">Rec. No. :</td>
                <td class="value"><b>{{ receiptData.receipt_no || receiptData.rec_no || '-' }}</b></td>
                <td class="label">Adm. No. :</td>
                <td class="value"><b>{{ receiptData.admission_form_no || receiptData.admission_no || '-' }}</b></td>
              </tr>
              <tr>
                <td class="label">Class :</td>
                <td class="value"><b>{{ receiptData.standard || receiptData.class || '-' }}</b></td>
                <td class="label">Date :</td>
                <td class="value"><b>{{ receiptData.receipt_date || '-' }}</b></td>
              </tr>
              <tr>
                <td class="label">Admitted for :</td>
                <td class="value"><b>{{ receiptData.admitted_for || receiptData.ay || '-' }}</b></td>
                <!-- <td class="label empty-cell"></td>
                <td class="value empty-cell"></td> -->

                <td class="label">Student Id :</td>
                <td class="value"><b>{{ receiptData.student_id || receiptData.registration_number || '-' }}</b></td>
              </tr>
              <tr>
                <td class="label">Roll No :</td>
                <td class="value"><b>{{ receiptData.roll_no || '-' }}</b></td>

                <td class="label">Name :</td>
                <td colspan="3" class="value name-full"><b>{{ safeName }}</b></td>
              </tr>
            </table>

            <div class="fee-header">Received the following</div>

            <table class="fee-table" cellpadding="0" cellspacing="0">
              <tbody>
                <tr v-for="item in receiptData.student_receipt_details || []" :key="item.id || item.component">
                  <td class="fee-desc">{{ item.component || item.fee_head || 'Unknown Fee' }}</td>
                  <td class="fee-amount">{{ formatAmount(item.amount) }}</td>
                </tr>
              </tbody>
            </table>

            <div class="total-line">
              <span class="total-label"><b>TOTAL :</b></span>
              <span class="total-value">₹ {{ safeTotalAmountNoSymbol }}.00</span>
            </div>

            <div class="bottom-section">
              <div class="bottom-line">In words : <b>{{ receiptData.amount_in_words || '-' }}</b></div>
              <div class="bottom-line">Amount : {{ formatAmount(receiptData.total_fees_amount, false) }}.00</div>
              <div class="bottom-line">Remarks : {{ receiptData.remark || '-' }}</div>

              <!-- Signature block – right aligned -->
              <div class="signature-block">
                <div class="signature-line-wrapper">
                  <!-- <div class="signature-line"></div> -->
                  <div class="sig-text">RECEIVER'S SIGNATURE</div>
                </div>

                <!-- <div class="receiver-info">
                  N1-RADNYA SUMANT{{ receiptData.print_date ? ' ' + receiptData.print_date : '' }}<br>
                  NEMSADMIN
                </div> -->
              </div>

              <!-- Horizontal line separator -->
              <!-- <hr class="separator-line" /> -->

              <!-- Page info -->
              <div class="footer-info">
                <div class="footer-left">
                  {{ receiptData.print_date ? receiptData.print_date : ''}}
                </div>
                <!-- <div class="footer-center">
                  {{ receiptData.print_username || 'NEMSADMIN' }}
                </div> -->
                <div class="footer-right">
                  Page 1 of 1
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="text-center pa-12">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        <div class="mt-4">Loading receipt data...</div>
      </div>
    </v-card>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "DesFeeReceipt",
  data: () => ({
    receiptData: null,
    receipt_no: null,
    ay: null,
    registration_no: null,
    selectedCopy: "both",
    org_basic_data: {},
  }),

  computed: {
    safeName() {
      return (this.receiptData.name || "-").toUpperCase();
    },
    safeTotalAmountNoSymbol() {
      const amt = Number(this.receiptData.total_fees_amount || 0);
      return amt.toLocaleString("en-IN");
    },
  },

  created() {
    this.receipt_no = JSON.parse(this.$session.get("receipt_no") || "null");
    this.ay = JSON.parse(this.$session.get("ay") || "null");
    this.registration_no = JSON.parse(this.$session.get("registration_no") || "null");
  },

  mounted() {
    this.fetchReceiptData();
  },

  methods: {
    async fetchReceiptData() {
      try {
        const payload = {
          receipt_no: this.receipt_no,
          ay: this.ay,
          registration_no: this.registration_no,
        };
        const res = await axios.post("/AccountingAndFeesReports/getFeeReceipts", payload);
        if (res.data.flag === "YES") {
          this.receiptData = res.data.student_receipt_details;
          this.org_basic_data = res.data.org_basic_data || {};
        }
      } catch (err) {
        console.error("Failed to load receipt:", err);
      }
    },

    formatAmount(value, withComma = true) {
      if (!value) return "-";
      const num = Number(value);
      if (isNaN(num)) return "-";
      return withComma ? num.toLocaleString("en-IN") : num.toLocaleString("en-IN", { useGrouping: false });
    },

    printReceipt() {
      const printArea = document.getElementById("des-receipt-print-area");
      if (!printArea) return;

      const printContent = printArea.innerHTML;
      const original = document.body.innerHTML;

      document.body.innerHTML = printContent;
      window.print();
      document.body.innerHTML = original;
      window.location.reload();
    },
  },
};
</script>

<style scoped>
.receipt-outer {
  width: 100%;
  max-width: none;
  margin: 0 auto;
  font-family: "Times New Roman", Times, serif;
  font-size: 12px;
  line-height: 1.15;
  box-sizing: border-box;
}

.green-dotted {
  border: 2px dotted #006400;
  padding: 6px;
}

.receipt-copy {
  flex: 1;
  padding: 4px 8px;
}

.receipt-outer {
  display: flex;
  flex-direction: row;
}

.full-width {
  flex: none;
  width: 100% !important;
}

.partition-line {
  width: 1px;
  background: repeating-linear-gradient(to bottom, #000 0, #000 2px, transparent 2px, transparent 4px);
}

.copy-label {
  text-align: right;
  margin-left: 430px;
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
  margin: 6px 0 6px;
  color: #767474;
}

/* ── Header ──────────────────────────────────────────────── */
.header-block {
  margin-bottom: 8px;
}

.trust-gst-line {
  display: flex;
  justify-content: flex-end; /* pushes GST to right */
  align-items: center;
}

.trust-name {
  font-weight: bold;
}

.gst-number {
  white-space: nowrap;   /* 👈 key fix */
  font-size: 11px;
  font-weight: bold;
}


.student-gst {
  text-align: left;
}

.school-header-row {
  display: flex;
  align-items: center;
}

.school-logo-wrapper {
  width: 70px;
  flex-shrink: 0;
}

.school-logo {
  max-width: 85px;
  max-height: 85px;
  object-fit: contain;
}

.school-info-center {
  text-align: center;
  flex: 1;
  padding: 0 12px;
}

.school-name {
  font-size: 15px;
  font-weight: bold;
  margin: 0 0 2px 0;
  line-height: 1.1;
}

.school-address {
  font-size: 10px;
  line-height: 1.2;
}

.right-placeholder {
  width: 70px;
  flex-shrink: 0;
}

.receipt-title-line {
  text-align: center;
  font-size: 11px;
  font-weight: bold;
  margin: 6px 0 4px 0;
  border-top: 1px solid #000;
  border-bottom: 1px solid #000;
  padding: 3px 0;
}

/* ── Info table & below ─────────────────────────────────── */
.info-table {
  width: 100%;
  border-collapse: collapse;
  margin: 8px 0 12px;
}

.info-table td {
  padding: 2px 4px;
  vertical-align: top;
  font-size: 12px;
}

.info-table .label {
  width: 22%;
  font-weight: bold;
  white-space: nowrap;
}

.info-table .value {
  font-weight: bold;
}

.info-table .name-full {
  font-weight: bold;
  padding-top: 4px;
}

.info-table .empty-cell {
  border: none;
}

.fee-header {
  font-weight: bold;
  margin: 6px 0 8px;
  /* slightly increased bottom margin */
}

.fee-table {
  width: 100%;
  border-top: 1px solid #000;
  /* border-bottom removed on purpose */
  margin-bottom: 24px;
  /* controls space before TOTAL section */
}

.fee-table td {
  padding: 3px 6px;
  /* slightly taller rows */
  vertical-align: middle;
}

.fee-table:has(tr:first-child:last-child) {
  /* only works in modern browsers — very clean */
  margin-bottom: 282px;
  /* much more space when exactly 1 row */
}

.fee-table:has(tr:nth-child(2):last-child) {
  margin-bottom: 30px;
  /* still generous for 2 rows */
}

.fee-desc {
  width: 68%;
}

.fee-amount {
  text-align: right;
  font-weight: bold;
}

.total-line {
  display: flex;
  justify-content: flex-end;
  margin: 12px 0 16px;
  /* good breathing room around total box */
  font-weight: bold;
  font-size: 13px;
  border-top: 1px solid #000;
  /* line above TOTAL */
  border-bottom: 1px solid #000;
  /* line below TOTAL */
  padding: 6px 0;
  /* space inside the box */
}

.total-label {
  padding-right: 40px;
}

.total-value {
  min-width: 100px;
  text-align: right;
  padding-right: 6px;
}

.bottom-section {
  font-size: 11px;
  margin-top: 12px;
}

/* Each info line */
.bottom-line {
  margin: 1px 0;
  line-height: 1.3;
  font-weight: bold;
}

/* Signature block – everything right-aligned */
.signature-block {
  text-align: right;
  margin: 20px 0 10px 0;
  padding-right: 20px;
}

/* Wrapper for line + text */
.signature-line-wrapper {
  display: inline-block;
  text-align: right;
  width: 280px;
}

/* The underline itself */
.signature-line {
  border-bottom: 1px solid #000;
  width: 100%;
  margin-bottom: 3px;
  margin-left: auto;
}

/* "RECEIVER'S SIGNATURE" text */
.sig-text {
  font-weight: bold;
  font-size: 11px;
  margin-top: 2px;
}

.page-info {
  text-align: right;
  font-size: 10px;
  margin-top: 8px;
  border-top: 1px solid #000;
  padding-top: 3px;
}

.footer-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 10px;
  font-weight: bold;
  margin-top: 16px;
  padding: 6px 0;
  border-top: 1px solid #000;
}

.footer-left {
  flex: 1;
  text-align: left;
}

.footer-center {
  flex: 1;
  text-align: center;
}

.footer-right {
  flex: 1;
  text-align: right;
}

@media print {
  @page {
    size: landscape;
    margin: 2mm;
  }

  body {
    margin: 0;
  }

  .no-display,
  .v-toolbar,
  .v-btn,
  .v-input,
  header,
  footer,
  nav {
    display: none !important;
  }

  /* Force side-by-side layout */
  .receipt-outer {
    display: flex !important;
    flex-direction: row !important;
    width: 100% !important;
  }

  .receipt-copy {
    width: 50% !important;
  }
.receipt-body {
  min-height: auto;
}

  .full-width {
    width: 100% !important;
  }

  .partition-line {
    display: block !important;
  }
}

</style>